import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requestsService, Request } from "../services/requestsService";
import { useSelector } from "react-redux";
import { selectUserId } from "../store/slices/authUserSlice";

const RequestsList: React.FC = () => {
  const [requests, setRequests] = useState<Request[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const userId = useSelector(selectUserId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    setIsLoading(true);
    requestsService.getRequests()
      .then((data) => {
        setRequests(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching requests:", error);
        setIsLoading(false);
      });
  }, [userId]);

  // Group requests by date (YYYY-MM-DD)
  const groupedRequests = requests.reduce((groups: Record<string, Request[]>, request) => {
    const date = new Date(request.created_at);
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(request);
    return groups;
  }, {});

  // Sort dates newest first
  const sortedDates = Object.keys(groupedRequests).sort((a, b) => {
    return new Date(b).getTime() - new Date(a).getTime();
  });

  const formatRequestType = (type: string) => {
    switch(type) {
      case 'voice_note':
        return 'Voice Note';
      case 'link':
        return 'Link';
      case 'document':
        return 'Document';
      case 'self_interview':
        return 'AI Interview';
      case 'professional_interview':
        return 'Professional Interview';
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch(type) {
      case 'voice_note':
        return 'list-tag-purple';
      case 'link':
        return 'list-tag-blue';
      case 'document':
        return 'list-tag-green';
      case 'self_interview':
        return 'list-tag-purple';
      case 'professional_interview':
        return 'list-tag-purple';
      default:
        return 'list-tag-gray';
    }
  };

  const getRequestTitle = (request: Request) => {
    return `${formatRequestType(request.request_type)} Request`;
  };

  return (
    <div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: "24px"
      }}>
        <h1>Your Requests</h1>
        <button
          className="primary-button"
          onClick={() => navigate('/requests')}
        >
          Create New Request
        </button>
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      ) : requests.length === 0 ? (
        <div className="empty-state-container">
          <h3>No requests found</h3>
          <p>Create a new request to get started.</p>
        </div>
      ) : (
        <div>
          {sortedDates.map((date) => {
            const [year, month, day] = date.split('-').map(num => parseInt(num, 10));
            const localDate = new Date(year, month - 1, day);
            return (
              <div key={date} className="date-group">
                <h3 className="date-heading">{localDate.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</h3>
                <ul className="newsletter-list">
                  {groupedRequests[date].map((request) => (
                    <li
                      key={request.id}
                      className="newsletter-item"
                      onClick={() => navigate(`/requests/view/${request.id}`)}>
                      <div className="newsletter-item-content-left">
                        <Link
                          to={`/requests/view/${request.id}`}
                          className="newsletter-title"
                          style={{ textDecoration: 'none' }}
                        >
                          {getRequestTitle(request)}
                        </Link>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "6px",
                          paddingLeft: "12px"
                        }}>
                          <span className={getTypeColor(request.request_type)} style={{
                            fontSize: "0.75rem",
                            padding: "4px 8px"
                          }}>
                            {formatRequestType(request.request_type)}
                          </span>
                          <span className="list-tag-gray" style={{
                            fontSize: "0.75rem",
                            padding: "4px 8px"
                          }}>
                            {request.user.name || request.user.email}
                          </span>
                          <span className="list-tag-gray" style={{
                            fontSize: "0.75rem",
                            padding: "4px 8px"
                          }}>
                            {new Date(request.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}>
                        <button
                          className="tertiary-info-button"
                          style={{
                            cursor: "pointer",
                            marginRight: "12px",
                          }}
                        >
                          View
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          )}
        </div>
      )}
    </div>
  );
};

export default RequestsList;